<script setup lang="ts">
// One date column's filter as a trigger plus a popout. The two date inputs and
// the two day presets used to sit inline in the filter row, which is 22px tall
// and as wide as one column: the inputs alone filled it, and the row read as a
// wall of date boxes. Here the row carries one trigger that says what the range
// IS ("Any", "30 Jul 2026", "26 Jul – 30 Jul 2026"), and the inputs and the
// presets live in the panel behind it.
//
// Presentational, like the enum control beside it: the parent owns the clause.
// This component emits the range it was given, or null when cleared, and the
// parent builds the one dateRange clause through the path the inline inputs
// used, so the wire shape is unchanged.
import { computed, nextTick, onBeforeUnmount, ref } from "vue";

import {
	type MapReviewColumn,
	formatDayLabel,
	localDayString,
	startOfLocalWeek,
} from "./columns";

interface Props {
	column: MapReviewColumn;
	/** The column's current range, as the wire carries it: "YYYY-MM-DD" each,
	 * empty when unset. */
	from: string;
	to: string;
}
const props = defineProps<Props>();
const emit = defineEmits<{
	change: [range: { from: string | null; to: string | null } | null];
}>();

// Teleported to <body> and positioned against the trigger, as the enum panel
// is: inside the table's scroll container the panel would be clipped.
const PANEL_MIN_WIDTH = 240;

const open = ref(false);
const rootEl = ref<HTMLElement | null>(null);
const triggerEl = ref<HTMLButtonElement | null>(null);
const panelEl = ref<HTMLElement | null>(null);
const panelStyle = ref<Record<string, string>>({});

const hasRange = computed(() => props.from !== "" || props.to !== "");
const summary = computed(() => {
	const { from, to } = props;
	if (from !== "" && to !== "")
		return from === to
			? formatDayLabel(from)
			: `${formatDayLabel(from)} – ${formatDayLabel(to)}`;
	if (from !== "") return `from ${formatDayLabel(from)}`;
	if (to !== "") return `until ${formatDayLabel(to)}`;
	return "";
});
const title = computed(() =>
	hasRange.value
		? `${props.column.label}: ${summary.value}`
		: `Filter ${props.column.label} by date`,
);

// Both presets are the viewer's own days, and both carry the viewer's zone so
// the server reads them that way — a UTC day would end mid-afternoon locally.
function applyRange(from: string | null, to: string | null): void {
	emit("change", from === null && to === null ? null : { from, to });
}

function onInput(which: "from" | "to", event: Event): void {
	const value = (event.target as HTMLInputElement).value;
	const from = which === "from" ? value || null : props.from || null;
	const to = which === "to" ? value || null : props.to || null;
	applyRange(from, to);
}

function onPreset(preset: "today" | "week"): void {
	const today = localDayString(new Date());
	applyRange(preset === "today" ? today : startOfLocalWeek(), today);
	closePanel(true);
}

function clear(): void {
	emit("change", null);
	closePanel(true);
}

function positionPanel(): void {
	const trigger = triggerEl.value;
	if (!trigger) return;
	const rect = trigger.getBoundingClientRect();
	const below = window.innerHeight - rect.bottom - 6;
	const above = rect.top - 6;
	const openUp = below < 180 && above > below;
	const width = Math.max(rect.width, PANEL_MIN_WIDTH);
	const left = Math.min(
		Math.max(4, rect.left),
		Math.max(4, window.innerWidth - width - 4),
	);
	panelStyle.value = {
		position: "fixed",
		left: `${left}px`,
		width: `${width}px`,
		...(openUp
			? { bottom: `${window.innerHeight - rect.top + 2}px` }
			: { top: `${rect.bottom + 2}px` }),
	};
}

async function openPanel(): Promise<void> {
	open.value = true;
	await nextTick();
	positionPanel();
	panelEl.value?.querySelector<HTMLInputElement>("input")?.focus();
	// The listeners live exactly as long as the panel does, so nothing listens
	// on the document for a filter row nobody opened.
	bindListeners();
}

function closePanel(returnFocus = false): void {
	if (!open.value) return;
	open.value = false;
	unbindListeners();
	if (returnFocus) triggerEl.value?.focus();
}

function onTriggerClick(): void {
	if (open.value) closePanel(true);
	else void openPanel();
}

function onDocumentPointerDown(event: PointerEvent): void {
	const target = event.target as Node | null;
	if (!target) return;
	if (rootEl.value?.contains(target) || panelEl.value?.contains(target)) return;
	closePanel();
}

function onDocumentKeydown(event: KeyboardEvent): void {
	if (event.key === "Escape") closePanel(true);
}

function bindListeners(): void {
	document.addEventListener("pointerdown", onDocumentPointerDown, true);
	document.addEventListener("keydown", onDocumentKeydown);
}

function unbindListeners(): void {
	document.removeEventListener("pointerdown", onDocumentPointerDown, true);
	document.removeEventListener("keydown", onDocumentKeydown);
}

onBeforeUnmount(unbindListeners);
</script>

<template>
	<div ref="rootEl" class="dr-root">
		<button
			ref="triggerEl"
			type="button"
			class="dr-trigger"
			:class="{ 'dr-open': open, 'dr-empty': !hasRange }"
			:aria-expanded="open"
			:aria-label="title"
			:title="title"
			@click="onTriggerClick"
		>
			<span class="dr-summary">{{ summary || "Any" }}</span>
			<i class="bi bi-calendar3 dr-icon" aria-hidden="true"></i>
		</button>
		<button
			type="button"
			class="dr-clear"
			:class="{ 'dr-clear-off': !hasRange }"
			:tabindex="hasRange ? 0 : -1"
			:aria-hidden="!hasRange"
			:aria-label="`Clear ${column.label} date filter`"
			:title="`Clear ${column.label} date filter`"
			@click="clear"
		>
			<i class="bi bi-x" aria-hidden="true"></i>
		</button>

		<Teleport to="body">
			<div
				v-if="open"
				ref="panelEl"
				class="dr-panel"
				:style="panelStyle"
				role="group"
				:aria-label="`Filter ${column.label} by date`"
			>
				<label class="dr-field">
					<span class="dr-field-label">From</span>
					<input
						type="date"
						class="dr-input"
						:value="from"
						@change="onInput('from', $event)"
					/>
				</label>
				<label class="dr-field">
					<span class="dr-field-label">To</span>
					<input
						type="date"
						class="dr-input"
						:value="to"
						@change="onInput('to', $event)"
					/>
				</label>
				<div class="dr-presets">
					<button type="button" class="dr-preset" @click="onPreset('today')">
						Today
					</button>
					<button type="button" class="dr-preset" @click="onPreset('week')">
						This week
					</button>
				</div>
			</div>
		</Teleport>
	</div>
</template>

<style scoped>
.dr-root {
	display: flex;
	align-items: center;
	gap: 2px;
	min-width: 0;
}

.dr-trigger {
	display: flex;
	align-items: center;
	gap: 3px;
	min-width: 0;
	height: 22px;
	padding: 0 4px;
	font-size: 0.75rem;
	color: #1b1f24;
	background: #fff;
	border: 1px solid #ced4da;
	border-radius: 2px;
	cursor: pointer;
}

.dr-summary {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.dr-empty .dr-summary {
	color: #667085;
}

.dr-icon {
	flex: 0 0 auto;
	color: #667085;
	font-size: 0.6875rem;
}

.dr-trigger:hover,
.dr-open {
	border-color: #94a3b8;
}

/* The clear holds its slot whether or not a range is set, so setting one cannot
   move the trigger out from under a pointer already resting on it. */
.dr-clear {
	flex: 0 0 auto;
	width: 16px;
	border: 0;
	padding: 0 2px;
	background: transparent;
	color: #667085;
	line-height: 1;
	cursor: pointer;
}

.dr-clear-off {
	visibility: hidden;
}

.dr-clear:hover {
	color: #1b1f24;
}

.dr-panel {
	z-index: 40;
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 8px;
	background: #fff;
	border: 1px solid #ced4da;
	border-radius: 4px;
	box-shadow: 0 8px 24px -8px rgba(17, 24, 39, 0.24);
}

.dr-field {
	display: flex;
	align-items: center;
	gap: 6px;
}

.dr-field-label {
	width: 32px;
	font-size: 0.6875rem;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #667085;
}

.dr-input {
	flex: 1 1 auto;
	min-width: 0;
	height: 24px;
	padding: 0 4px;
	font-size: 0.75rem;
	color: #1b1f24;
	border: 1px solid #ced4da;
	border-radius: 2px;
}

.dr-presets {
	display: flex;
	gap: 4px;
}

.dr-preset {
	height: 24px;
	padding: 0 8px;
	font-size: 0.75rem;
	color: #1b1f24;
	background: #f1f3f5;
	border: 1px solid #ced4da;
	border-radius: 2px;
	cursor: pointer;
}

.dr-preset:hover {
	background: #e9ecef;
}
</style>
